import { useGetPurchasedReviewStats } from './review';

/**
 * Aggregate summary for the Excluded items landing page, from the dedicated
 * stats endpoint (`GET /purchased-goods/review/stats`).
 *
 * All figures are real and whole-org / all-time: item counts and spend per
 * bucket, plus the excluded share of ALL purchase spend as a 0–1 fraction
 * (undefined when the organisation has no spend data — the landing page hides
 * the caption in that case).
 */

/** Count + spend for one excluded-items category. */
export interface ExcludedCategorySummary {
  /** Number of excluded items in this category. */
  count: number;
  /** Total spend across those items. */
  spend?: number;
}

export interface ExcludedItemsSummary {
  /** All excluded items combined (review + other). */
  total: ExcludedCategorySummary;
  /** "Items for review" — unclear item type, match or weight; excluded until
   *  resolved. */
  review: ExcludedCategorySummary;
  /** "Other goods" — identified as neither food nor packaging. */
  other: ExcludedCategorySummary;
  /** Excluded spend as a fraction (0–1) of total purchase spend. Undefined
   *  when the organisation has no purchase spend. */
  excludedSpendProportion?: number;
}

export const useGetExcludedItemsSummary = (): {
  data: ExcludedItemsSummary | undefined;
  isLoading: boolean;
} => {
  const { data, isLoading } = useGetPurchasedReviewStats();

  if (data == null) {
    return { data: undefined, isLoading };
  }

  return {
    data: {
      review: { count: data.review.items, spend: data.review.spend },
      other: { count: data.other.items, spend: data.other.spend },
      total: { count: data.totalItems, spend: data.totalSpend },
      excludedSpendProportion: data.spendFraction ?? undefined,
    },
    isLoading,
  };
};
